/**
 * PedidoAutoPrintSettings — Auto-impresión de pedidos de tienda.
 *
 * QUÉ HACE: Muestra el toggle «Auto-imprimir pedidos de tienda», persistido vía
 * pedidoAutoPrint.service (transactionalStore, scoped por businessId). Vive
 * dentro de «Opciones avanzadas» de la config de impresora
 * (UserSettingsPosHardware).
 *
 * QUÉ EXPORTA: PedidoAutoPrintSettings
 *
 * QUÉ NO DEBE HACER:
 *  - No detecta ni imprime pedidos (eso es del watcher global en MainLayout).
 *  - No usa localStorage directo (prohibido): persiste por el service.
 *  - No configura avisos de sonido/voz/notificación: viven en «Sonidos del POS».
 */
import { useEffect, useState } from "react";
import { ShoppingBag } from "lucide-react";
import { SwitchRow } from "@/components/ui/Switch";
import { useAuthStore } from "@/store/authStore";
import {
  getPedidoAutoPrintPrefs,
  setPedidoAutoPrintPrefs,
  type PedidoAutoPrintPrefs,
} from "../services/pedidoAutoPrint.service";

/** Toggle de auto-impresión de pedidos de tienda. */
export function PedidoAutoPrintSettings() {
  const business = useAuthStore((s) => s.business);
  const businessId = business?.id ?? "default";
  const [prefs, setPrefs] = useState<PedidoAutoPrintPrefs>(() =>
    getPedidoAutoPrintPrefs(businessId),
  );

  useEffect(() => {
    setPrefs(getPedidoAutoPrintPrefs(businessId));
  }, [businessId]);

  const update = (patch: Partial<PedidoAutoPrintPrefs>) => {
    setPrefs(setPedidoAutoPrintPrefs(businessId, patch));
  };

  return (
    <div>
      <p className="text-[9px] font-black uppercase tracking-widest text-gray-400 dark:text-slate-500 mb-2 px-0.5">
        Pedidos de tienda
      </p>
      <div className="space-y-2">
        <SwitchRow
          icon={<ShoppingBag size={16} />}
          label="Auto-imprimir pedidos de tienda"
          description="Imprime el ticket solo al llegar un pedido «para el local», sin importar la pantalla en la que estés."
          checked={prefs.autoPrintEnabled}
          onChange={(v) => update({ autoPrintEnabled: v })}
        />
      </div>
      <p className="mt-2 px-0.5 text-[10px] text-gray-500 dark:text-slate-400 leading-snug">
        Si la impresora no está disponible, el pedido queda en cola y se imprime
        solo al configurarla. El sonido, la voz y la notificación de pedidos
        nuevos se configuran en «Sonidos del POS».
      </p>
    </div>
  );
}
